import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import UploadFile from "../../../../common/UploadFile/UploadFile";
import Layout from "../../../../common/layouts/Layout";
import Navbar from "../../../../common/Navbar/Navbar";
import {
  useGetEcSubmissionByIdInfoLazyQuery,
  useGetEcSubmissionByIdInfoQuery,
  useRemoveEcsMutation,
} from "../../../../../generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../../../states/zustand/authStore";
import ReadReviewDialog from "../../../../common/ReadReviewDialog/ReadReviewDialog";
import WithPermission from "../../../../common/WithPermission/WithPermission";
import { ROLES, RoutingList, SUBMISSION_TYPES } from "../../../../../constants";
import UploadFileAgain from "../../../../common/UploadFileAgain/UploadFileAgain";
import { romanize } from "../../../../../utils/helper";
import useAppStore from "../../../../../states/zustand/appStore";
import { toast } from "react-toastify";

const ECInitialReviewDetails: FC = (): JSX.Element => {
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const { profileData } = useAuthStore((state) => state);
  const navigate = useNavigate();
  const { id } = useParams() as any;
  const [removeECS] = useRemoveEcsMutation();
  const [getECSubmission, { data, loading }] =
    useGetEcSubmissionByIdInfoLazyQuery();

  const { setLoader } = useAppStore();

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const fetchDetail = useCallback(() => {
    return getECSubmission({
      variables: { id: id },
    });
  }, [id]);
  useEffect(() => {
    fetchDetail();
  }, [id]);

  const [selectedDoc, setSelectedDoc] = useState<{
    docId: string;
    label: string;
  }>({
    docId: "",
    label: "",
  });

  const handleEdit = () => {
    navigate(RoutingList.ethics_committee.edit.path.replace(":id", id));
  };

  const openReview = (doc: { docId: string; label: string }) => {
    if (doc.docId) {
      setSelectedDoc(doc);
      setOpenReviewDialog(true);
    }
  };

  const handleDelete = async () => {
    try {
      let res = await removeECS({
        variables: {
          _id: id,
        },
      });
      if (!res.data) throw null;
      navigate(RoutingList.ethics_committee.index.path);
      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    }
  };

  return (
    <Layout>
      <>
        {selectedDoc && (
          <ReadReviewDialog
            open={openReviewDialog}
            onClose={fetchDetail}
            setOpen={setOpenReviewDialog}
            docId={selectedDoc.docId}
            title={selectedDoc.label}
            submissionType={SUBMISSION_TYPES.EC}
            submissionSubType={data?.ecSubmission?.type || ""}
          />
        )}
        <Navbar pageTitle="EC Submission" />
        <Container maxWidth={false}>
          <Card>
            <CardContent className={`pd-x-30 pd-y-30`}>
              <div
                className={` d-flex align-items-center justify-content-between`}
              >
                <Typography
                  variant={"body1"}
                  component={"p"}
                  className={`font-size-lg font-weight-bold`}
                >
                  {data?.ecSubmission?.studyDetails?.title} - #
                  {data?.ecSubmission?.studyDetails?.protocolNumber}
                </Typography>
                <WithPermission roles={[ROLES.INVESTIGATOR]}>
                  <div>
                    {/* <Button
                      variant={"outlined"}
                      color={"error"}
                      className="mg-r-10 btn-sm"
                      type="button"
                      onClick={handleEdit}
                    >
                      <i className={`icon-edit mg-r-10`}></i>
                      <span>Edit</span>
                    </Button> */}
                    <Button
                      variant={"outlined"}
                      color={"error"}
                      className="btn-sm"
                      type="button"
                      onClick={handleDelete}
                    >
                      <i className={`icon-trash mg-r-10`}></i>
                      <span>Delete</span>
                    </Button>
                  </div>
                </WithPermission>
              </div>
              <Divider className={`mg-t-30 mg-b-20`} />
              <section>
                <Typography
                  variant={"h2"}
                  component={"h2"}
                  className={`mg-b-20`}
                >
                  1. Study Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Protocol Number
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.protocolNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Study Title
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Sponsor
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.sponsor}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Principal Investigator
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.principalInvestigator}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Department
                      {/* <Typography
                        variant={"body2"}
                        component={"span"}
                        color={"secondary"}
                      >
                        (EC, CRC and PI)
                      </Typography> */}
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.department}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Email
                      <Typography
                        variant={"body2"}
                        component={"span"}
                        color={"secondary"}
                      >
                       {" "} (EC, CRC and PI)
                      </Typography>
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails.emails.join(", ")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Study Phase
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.studyPhase}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Type of Study
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.typeOfStudy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Funding / Grant
                    </Typography>
                    <div className={`d-flex align-items-center`}>
                      <div className={`bg-light uploaded-info pd-x-20 mg-r-10`}>
                        {/* {data?.irb?.isFunded ? "Yes" : "No"} */}
                        {data?.ecSubmission.studyDetails.isFunded ? "Yes" : "No"}
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Type of Funding
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.fundType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      Type Of Submission
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.type}{" "}
                      {data?.ecSubmission?.sub_type &&
                        `(${data?.ecSubmission?.sub_type})`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant={"h4"}
                      component={"h4"}
                      className={`mg-b-6`}
                    >
                      MICR #
                    </Typography>
                    <Typography
                      variant={"body2"}
                      component={"p"}
                      color={"secondary"}
                      className={`font-weight-semi-bold`}
                    >
                      {data?.ecSubmission?.studyDetails?.micr}
                    </Typography>
                  </Grid>

                  {/*<Grid item xs={12} md={3}>*/}
                  {/*    <Typography variant={"h4"} component={"h4"} className={`mg-b-6`}>Funding / Grant</Typography>*/}
                  {/*    <div className={`d-flex align-items-center`}>*/}
                  {/*        <div className={`bg-light uploaded-info pd-x-20 mg-r-10`}>*/}
                  {/*            Yes*/}
                  {/*        </div>*/}
                  {/*        <Typography variant={"body2"} component={"p"} color={"secondary"} className={`font-weight-semi-bold`}>(Lorem ipsum dolor sit amet, consectetur adipiscing elit)</Typography>*/}
                  {/*    </div>*/}
                  {/*</Grid>*/}
                </Grid>
              </section>
              <Divider className={`mg-y-30`} />
              <section>
                <Typography
                  variant={"h2"}
                  component={"h2"}
                  className={`mg-b-20`}
                >
                  2. EC Submission
                </Typography>
                <ul className={`list-style-none mg-b-10 submissionList`}>
                  {data?.ecSubmission?.documents?.map((d, i) => {
                    return (
                      <li key={d._id}>
                        <Grid
                          container
                          spacing={2}
                          className={`align-items-center justify-content-between`}
                        >
                          <Grid item xs={12} md={"auto"}>
                            <label className="form-label mg-b-0 font-size-14">
                              {romanize(i + 1)}). {d.label} :
                            </label>
                          </Grid>
                          <Grid item xs={12} md={"auto"}>
                            <div
                              className={`d-flex justify-content-between align-items-center`}
                            >
                              <div>
                                <WithPermission roles={[ROLES.INVESTIGATOR]}>
                                  <UploadFileAgain
                                    submissionType={SUBMISSION_TYPES.EC}
                                    name={d.label || ""}
                                    protocolNumber={
                                      data.ecSubmission.studyDetails
                                        .protocolNumber
                                    }
                                    docId={d._id}
                                    uploadBtnText={`Upload Again`}
                                  />
                                </WithPermission>
                              </div>
                              <Button
                                variant={"outlined"}
                                color={"secondary"}
                                size={"small"}
                                className={`pd-x-12 mg-l-10 font-weight-medium text-nowrap`}
                                onClick={() =>
                                  openReview({
                                    docId: d._id || "",
                                    label: d.label || "",
                                  })
                                }
                              >
                                <i className="icon-chat font-size-lg mg-r-10"></i>
                                {profileData.role === ROLES.INVESTIGATOR
                                  ? "Read"
                                  : "Read & Review"}
                              </Button>
                            </div>
                          </Grid>
                        </Grid>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </CardContent>
          </Card>
        </Container>
      </>
    </Layout>
  );
};

export default ECInitialReviewDetails;
